/*
 * Build-time-only network shim.
 *
 * While react-snap prerenders the site (potentially hundreds of pages), every
 * page would otherwise call the backend API — which trips its rate limit
 * (HTTP 429) and leaves pages half-rendered. Instead the build writes ALL blogs
 * to a single static file (`/seo-data.json`, produced by scripts/prerender.js)
 * and this shim answers the app's API calls from that file.
 *
 * It ONLY activates under react-snap's user agent, so real users in a normal
 * browser always hit the real API.
 */

function jsonResponse(body, status = 200) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

// Map an API request to a response body using the prefetched data.
// Returns `undefined` if the request isn't one we can serve (caller falls back
// to the real network).
function resolveFromData(urlStr, data) {
  let url;
  try {
    url = new URL(urlStr, window.location.origin);
  } catch (_) {
    return undefined;
  }

  const apiIdx = url.pathname.indexOf('/api/');
  if (apiIdx === -1) return undefined;

  const rest = url.pathname.slice(apiIdx + '/api/'.length).replace(/\/+$/, '');
  const segs = rest.split('/').filter(Boolean).map(decodeURIComponent);
  const q = url.searchParams;

  const blogsList = data.blogsList || [];
  const blogsById = data.blogsById || {};

  if (segs[0] !== 'blogs') return undefined;

  // /api/blogs/featured  → the pinned post as a one-item array, or [] when
  // nothing is pinned. Must be matched BEFORE the single-post branch below, which
  // would otherwise read "featured" as a blogId and answer 404. Never 404s and
  // never returns two, matching the real endpoint.
  if (segs.length === 2 && segs[1] === 'featured') {
    const pinned = blogsList.find((b) => b.featured);
    return pinned ? [pinned] : [];
  }

  // /api/blogs/<id>/related  → posts in the same category
  if (segs.length >= 3 && segs[2] === 'related') {
    const blog = blogsById[segs[1]];
    if (!blog) return [];
    const limit = parseInt(q.get('limit') || '3', 10);
    return blogsList
      .filter((b) => b.blogId !== blog.blogId && b.category === blog.category)
      .slice(0, limit);
  }

  // /api/blogs/<id>  → a single post
  if (segs.length >= 2) {
    return blogsById[segs[1]] || null;
  }

  // /api/blogs  → a cursor-paged window, matching the real contract
  const category = q.get('category');
  const limit = parseInt(q.get('limit') || '12', 10);

  let list = blogsList;
  if (category && category !== 'All') list = list.filter((b) => b.category === category);

  // The cursor is opaque to the app, but this shim is the thing that issued it,
  // so it is allowed to look inside its own key.
  let start = 0;
  const rawCursor = q.get('lastEvaluatedKey');
  if (rawCursor) {
    try {
      const cursor = JSON.parse(rawCursor);
      const index = list.findIndex((b) => String(b.blogId) === String(cursor.blogId));
      if (index === -1) return { content: [], lastEvaluatedKey: null, hasMore: false, size: 0 };
      start = index + 1;
    } catch (_) {
      /* unparseable cursor — start from the beginning */
    }
  }

  const window = list.slice(start, start + limit);
  const lastRead = window[window.length - 1];
  const hasMore = start + limit < list.length;

  return {
    content: window,
    lastEvaluatedKey:
      hasMore && lastRead ? { blogId: lastRead.blogId, createdOn: lastRead.createdOn } : null,
    hasMore,
    size: window.length,
  };
}

export function installPrerenderFetch() {
  const isPrerender =
    typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
  if (!isPrerender || typeof window === 'undefined' || !window.fetch) return;

  const originalFetch = window.fetch.bind(window);
  const dataPromise = originalFetch('/seo-data.json')
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  window.fetch = async (input, init) => {
    const urlStr =
      typeof input === 'string' ? input : (input && input.url) || String(input);

    if (urlStr.includes('/api/')) {
      const data = await dataPromise;
      if (data) {
        const body = resolveFromData(urlStr, data);
        if (body !== undefined) {
          return jsonResponse(body, body === null ? 404 : 200);
        }
      }
    }
    return originalFetch(input, init);
  };
}
